import { useLingui } from "@lingui/react/macro";
import { IconButton } from "@offstreetparking/design-system/forms";
import { DownloadIcon } from "@offstreetparking/design-system/icons/DownloadIcon";
import { FileIcon } from "@offstreetparking/design-system/icons/FileIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import saveAs from "file-saver";
import Image from "next/image";
import { useState } from "react";
import { WysiwygView } from "../../../forms/Wysiwyg/WysiwygView";
import { useKiosk } from "../../../providers/KioskProvider";
type ParkingInstructionsSectionProps = {
	instructions?: string | null;
	content: string;
	hasParcsCodes: boolean;
	files?: {
		id: number;
		fileName: string | null;
		publicUrl: string | null;
	}[];
	printPermit?: boolean;
};
export const ParkingInstructionsSection = ({
	instructions,
	content,
	hasParcsCodes,
	files,
	printPermit = false,
}: ParkingInstructionsSectionProps) => {
	const { t } = useLingui();
	const [downloadStatus, setDownloadStatus] = useState<string>("");
	const { isKiosk } = useKiosk();
	const handleDownload = (publicUrl: string, fileName: string) => {
		setDownloadStatus(t`Downloading ${fileName}`);
		saveAs(publicUrl, fileName);
		setTimeout(() => setDownloadStatus(""), 3000);
	};
	return (
		<div className="space-y-8 bg-neutral-faded px-4 py-4 [print-color-adjust:exact] sm:px-8">
			{!!instructions && (
				<>
					<Heading As="h3" kind="h3">
						{t`Parking instructions`}
					</Heading>
					<Text As="div" size="md">
						<WysiwygView content={content} />
					</Text>
				</>
			)}

			{hasParcsCodes && (
				<div className="flex items-stretch justify-between gap-4 text-left">
					<div className="flex flex-1 flex-col items-center gap-2 rounded-md border border-neutral-faded bg-white p-4 [print-color-adjust:exact]">
						{/* Alt image unnecessary due to instructions below */}
						<Image
							src="https://offstreet.s3.us-west-2.amazonaws.com/email/brightness-instructions-interchangable.png"
							width={120}
							height={56}
							sizes="120px"
							alt=""
						/>
						<Text className="font-normal text-xs xl:text-sm">
							{t`Turn the brightness up on your device.`}
						</Text>
					</div>
					<div className="flex flex-1 flex-col items-center gap-2 rounded-md border border-neutral-faded bg-white p-4 [print-color-adjust:exact]">
						{/* Alt image unnecessary due to instructions below */}
						<Image
							src="https://offstreet.s3.us-west-2.amazonaws.com/email/scan-instructions-interchangable.png"
							width={114}
							height={56}
							sizes="114px"
							alt=""
						/>
						<Text size="xs" className="font-normal xl:text-sm">
							{t`The gate should open when you approach it.`}
						</Text>
					</div>
				</div>
			)}

			<div className="flex items-stretch justify-between gap-4 text-left">
				<div className="flex flex-1 flex-col items-center gap-2 rounded-md border border-neutral-faded bg-white p-4">
					{/* Alt image unnecessary due to instructions below */}
					<Image
						src="https://offstreet.s3.us-west-2.amazonaws.com/email/plates-instructions-interchangeable.png"
						className="max-h-[56px] object-contain"
						width={120}
						height={56}
						sizes="120px"
						alt=""
					/>
					<Text className="font-normal text-xs xl:text-sm">
						{t`License plate must be facing the driving lane.`}
					</Text>
				</div>
				<div className="flex flex-1 flex-col items-center gap-2 rounded-md border border-neutral-faded bg-white p-4">
					{/* Alt image unnecessary due to instructions below */}
					<Image
						src={
							printPermit
								? "https://offstreet.s3.us-west-2.amazonaws.com/email/print-permit-instructions.png"
								: "https://offstreet.s3.us-west-2.amazonaws.com/email/digital-instructions-interchangable.png"
						}
						alt=""
						className="max-h-[56px] object-contain"
						width={120}
						height={56}
						sizes="120px"
					/>
					<Text size="xs" className="font-normal xl:text-sm">
						{printPermit
							? t`Please print this permit and display it on your dashboard.`
							: t`This is a virtual permit, do not print this off.`}
					</Text>
				</div>
			</div>

			{files && files.length > 0 && !isKiosk && (
				<div className="space-y-4">
					<Heading As="h3" kind="h3">
						{t`Attachments`}
					</Heading>

					<div aria-live="polite" aria-atomic="true" className="sr-only">
						{downloadStatus}
					</div>

					<div className="flex flex-col gap-4">
						{files.map((file) => (
							<div
								key={file.id}
								className="flex w-full items-center justify-between gap-2 rounded-sm border border-neutral-faded bg-white p-2 text-sm"
							>
								<div className="flex min-w-0 items-center gap-1">
									<FileIcon aria-hidden className="h-4 w-4 shrink-0" />
									<Text size="sm" className="truncate">
										{file.fileName}
									</Text>
								</div>
								<IconButton
									icon={DownloadIcon}
									intent="action"
									aria-label={t`Download ${file.fileName ?? ""}`}
									className="h-6 w-6 print:hidden"
									onClick={() => handleDownload(file.publicUrl!, file.fileName!)}
								/>
								<a
									href={file.publicUrl!}
									target="_blank"
									rel="noopener noreferrer"
									aria-label={t`Download ${file.fileName ?? ""}`}
									className="hidden h-6 w-6 items-center justify-center text-accent print:flex"
								>
									<DownloadIcon className="h-5 w-5" />
								</a>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
